import { useMutation } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import type { OrderStatus } from '@/bindings/OrderStatus'
import type { QueueOrder } from '@/bindings/QueueOrder'
import { Button } from '@/components/button'
import { ConfirmButton } from '@/components/confirm'
import { FormError } from '@/components/form-error'
import { moveOrder } from '@/features/back-office/api'
import { ACTIVE_STATUSES } from '@/features/back-office/queue-filter'
import { parseOrderStatus } from '@/features/ordering/order-rules'
import { claim } from '@/lib/api'
import type { MessageKey } from '@/lib/i18n/messages/en'
import { type Translate, useT } from '@/lib/i18n/translate'

/** The "next" button per status (T16 happy path): where it goes and what it says; nothing past READY. */
const NEXT: Partial<Record<OrderStatus, { to: OrderStatus; label: MessageKey }>> = {
  sent: { to: 'confirmed', label: 'queue.next.sent' },
  confirmed: { to: 'processing', label: 'queue.next.confirmed' },
  processing: { to: 'ready', label: 'queue.next.processing' },
  ready: { to: 'completed', label: 'queue.next.ready' },
}

/** The status an `ORDER_MOVED` refusal names, worded like the badge; a surprise value shows as-is. */
function statusWord(t: Translate, value: unknown): string {
  const status = parseOrderStatus(value)
  return status ? t(`track.status.${status}`) : String(value)
}

interface OrderActionsProps {
  restaurantId: string
  branchId: string
  order: QueueOrder
  /** The server's answer replaces the order at once (T8: never a guess). */
  onMoved: (moved: QueueOrder) => void
  /** `ORDER_MOVED`: the order was not where the page showed it; refetch. */
  onStale: () => void
  /** Sits between next and cancel: the details page's print and edit. */
  middle?: ReactNode
}

/**
 * The staff moves on one order (G9, T16): the one-step "next" on the left, cancel alone on
 * the right behind a second press (PRD: apart, so a wrong touch is not a cancel). A stale
 * page is told where the order went instead of claiming success. Nothing on a terminal order.
 */
export function OrderActions({ restaurantId, branchId, order, onMoved, onStale, middle }: OrderActionsProps) {
  const t = useT()
  const move = useMutation({
    mutationFn: (to: OrderStatus) => moveOrder(restaurantId, branchId, order.id, { to }),
    onSuccess: onMoved,
    onError: (error) => {
      if (claim(error, 'ORDER_MOVED')) onStale()
    },
  })
  const moved = claim(move.error, 'ORDER_MOVED')
  const next = NEXT[order.status]
  const active = ACTIVE_STATUSES.includes(order.status)

  return (
    <>
      {moved ? (
        <p role="status" className="text-warning text-sm print:hidden">
          {t('queue.moved', { status: statusWord(t, moved.params.status) })}
        </p>
      ) : (
        <FormError error={move.error} />
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        {next ? (
          <Button loading={move.isPending} disabled={moved !== undefined} onClick={() => move.mutate(next.to)}>
            {t(next.label)}
          </Button>
        ) : (
          <span />
        )}
        {middle}
        {active ? (
          <ConfirmButton
            label={t('track.cancel')}
            variant="danger-ghost"
            disabled={move.isPending || moved !== undefined}
            confirmLabel={t('track.cancelConfirm')}
            keepLabel={t('track.cancelKeep')}
            align="end"
            confirm={() => move.mutateAsync('canceled')}
          />
        ) : (
          <span />
        )}
      </div>
    </>
  )
}
